import { DescriptionOutlined, Mode, Visibility } from '@mui/icons-material';
import {
    Button,
    IconButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { ReactNode, useState } from 'react';

import Result from 'components/v2/common/Result';
import TableSkeleton from 'components/v2/common/tableSkeleton';
import DocumentDescriptionModal, {
    DocumentDescriptionInfoButton,
    DocumentDescriptionSource,
} from 'views/v2/Dashboards-B2B/components/backOfficeFlow/documents/components/documentDescriptionModal';

import { ACTIONS } from 'constant';
import useTranslate from 'hooks/useTranslate';
import { ClientCompanyDocumentTemplate } from 'types';

const headers = ['name', 'template', 'documentsSample', 'actions'];

const nameCellSx = {
    width: '100%',
    maxWidth: 0,
    overflow: 'hidden',
};

const fitCellSx = {
    width: '1%',
    whiteSpace: 'nowrap' as const,
};

interface Props {
    loading?: boolean;
    documentTemplates: ClientCompanyDocumentTemplate[];
    handleAction: (action: ACTIONS, documentTemplate: ClientCompanyDocumentTemplate) => void;
}

const getDisplayName = (documentTemplate: ClientCompanyDocumentTemplate) =>
    documentTemplate.name_en || documentTemplate.name_fr || '';

const toDescriptionSource = (
    documentTemplate: ClientCompanyDocumentTemplate
): DocumentDescriptionSource => ({
    document_name: getDisplayName(documentTemplate),
    description_en: documentTemplate.description_en,
    description_fr: documentTemplate.description_fr,
});

const actionButtonSx = { textTransform: 'none', flexShrink: 0 };

const MobileActionRow = ({ label, action }: { label: string; action: ReactNode }) => (
    <Stack
        direction="row"
        spacing={1}
        sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 0.75,
            borderTop: 1,
            borderColor: 'divider',
        }}
    >
        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 0 }}>
            {label}
        </Typography>
        {action}
    </Stack>
);

const EmptyAction = () => (
    <Typography variant="body2" color="text.disabled">
        -
    </Typography>
);

const DocumentTemplateTable = ({ documentTemplates, handleAction, loading = false }: Props) => {
    const { translate } = useTranslate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [descriptionDocument, setDescriptionDocument] =
        useState<DocumentDescriptionSource | null>(null);

    const descriptionModal = descriptionDocument ? (
        <DocumentDescriptionModal
            document={descriptionDocument}
            onClose={() => setDescriptionDocument(null)}
        />
    ) : null;

    if (loading) {
        return <TableSkeleton />;
    }

    if (isMobile) {
        if (!documentTemplates.length) {
            return (
                <Result
                    icon={<DescriptionOutlined sx={{ fontSize: 32 }} />}
                    title={translate('noOrganizationDocumentTemplates')}
                    description={translate('noOrganizationDocumentTemplatesDescription')}
                />
            );
        }

        return (
            <Stack spacing={1.5}>
                {documentTemplates.map((documentTemplate) => (
                    <Paper
                        key={documentTemplate.id}
                        variant="outlined"
                        sx={{ px: 2, pt: 1.5, pb: 0.5 }}
                    >
                        <Stack spacing={1} sx={{ pb: 0.5 }}>
                            <Stack
                                direction="row"
                                spacing={0.5}
                                sx={{ alignItems: 'center', minWidth: 0 }}
                            >
                                <Typography
                                    variant="subtitle1"
                                    sx={{
                                        fontWeight: 600,
                                        minWidth: 0,
                                        flex: 1,
                                        overflowWrap: 'anywhere',
                                    }}
                                >
                                    {getDisplayName(documentTemplate)}
                                </Typography>
                                <DocumentDescriptionInfoButton
                                    document={toDescriptionSource(documentTemplate)}
                                    onClick={() =>
                                        setDescriptionDocument(
                                            toDescriptionSource(documentTemplate)
                                        )
                                    }
                                />
                            </Stack>
                        </Stack>

                        <MobileActionRow
                            label={translate('template')}
                            action={
                                documentTemplate.template_uri ? (
                                    <Button
                                        size="small"
                                        startIcon={<Visibility fontSize="small" />}
                                        onClick={() =>
                                            handleAction(ACTIONS.PREVIEW_FILE, documentTemplate)
                                        }
                                        sx={actionButtonSx}
                                    >
                                        {translate('preview')}
                                    </Button>
                                ) : (
                                    <EmptyAction />
                                )
                            }
                        />
                        <MobileActionRow
                            label={translate('documentsSample')}
                            action={
                                documentTemplate.sample_uri ? (
                                    <Button
                                        size="small"
                                        startIcon={<Visibility fontSize="small" />}
                                        onClick={() =>
                                            handleAction(ACTIONS.PREVIEW_SAMPLE, documentTemplate)
                                        }
                                        sx={actionButtonSx}
                                    >
                                        {translate('preview')}
                                    </Button>
                                ) : (
                                    <EmptyAction />
                                )
                            }
                        />
                        {!documentTemplate.has_been_requested ? (
                            <MobileActionRow
                                label={translate('actions')}
                                action={
                                    <Button
                                        size="small"
                                        onClick={() =>
                                            handleAction(ACTIONS.SELECT, documentTemplate)
                                        }
                                        sx={actionButtonSx}
                                    >
                                        {translate('Request Document')}
                                    </Button>
                                }
                            />
                        ) : null}
                        <MobileActionRow
                            label={translate('edit')}
                            action={
                                <Button
                                    size="small"
                                    startIcon={<Mode fontSize="small" />}
                                    onClick={() => handleAction(ACTIONS.EDIT, documentTemplate)}
                                    sx={actionButtonSx}
                                >
                                    {translate('edit')}
                                </Button>
                            }
                        />
                    </Paper>
                ))}
                {descriptionModal}
            </Stack>
        );
    }

    if (!documentTemplates.length) {
        return (
            <Result
                icon={<DescriptionOutlined sx={{ fontSize: 32 }} />}
                title={translate('noOrganizationDocumentTemplates')}
                description={translate('noOrganizationDocumentTemplatesDescription')}
            />
        );
    }

    return (
        <>
            <Paper
                variant="outlined"
                sx={{
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                    height: 'clamp(360px, calc(100vh - 290px), 720px)',
                    borderRadius: 3,
                    boxShadow: '0 10px 30px rgba(15, 23, 42, 0.06)',
                }}
            >
                <TableContainer
                    sx={{
                        overflow: 'auto',
                        flex: 1,
                        minHeight: 0,
                        '&::-webkit-scrollbar': {
                            width: 10,
                            height: 10,
                        },
                        '&::-webkit-scrollbar-thumb': {
                            backgroundColor: 'rgba(148, 163, 184, 0.8)',
                            borderRadius: 999,
                            border: '2px solid transparent',
                            backgroundClip: 'padding-box',
                        },
                    }}
                >
                    <Table stickyHeader size="small" sx={{ width: '100%' }}>
                        <TableHead>
                            <TableRow>
                                {headers.map((value, index) => (
                                    <TableCell
                                        key={value}
                                        align={value === 'Actions' ? 'right' : 'left'}
                                        sx={{
                                            backgroundColor: 'grey.100',
                                            fontWeight: 700,
                                            textTransform: 'uppercase',
                                            letterSpacing: 0.6,
                                            fontSize: 12,
                                            color: 'text.secondary',
                                            ...(index === 0 ? nameCellSx : fitCellSx),
                                        }}
                                    >
                                        {translate(value)}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {documentTemplates.map((documentTemplate) => (
                                <TableRow
                                    key={documentTemplate.id}
                                    hover
                                    sx={{
                                        '&:nth-of-type(even)': {
                                            backgroundColor: 'rgba(148, 163, 184, 0.05)',
                                        },
                                        '& td': {
                                            borderBottomColor: 'rgba(148, 163, 184, 0.16)',
                                        },
                                    }}
                                >
                                    <TableCell sx={{ fontWeight: 600, ...nameCellSx }}>
                                        <Stack
                                            direction="row"
                                            spacing={0.5}
                                            sx={{ alignItems: 'center', minWidth: 0 }}
                                        >
                                            <Typography
                                                variant="body2"
                                                noWrap
                                                sx={{ fontWeight: 600, minWidth: 0 }}
                                            >
                                                {getDisplayName(documentTemplate)}
                                            </Typography>
                                            <DocumentDescriptionInfoButton
                                                document={toDescriptionSource(documentTemplate)}
                                                onClick={() =>
                                                    setDescriptionDocument(
                                                        toDescriptionSource(documentTemplate)
                                                    )
                                                }
                                            />
                                        </Stack>
                                    </TableCell>
                                    <TableCell sx={fitCellSx}>
                                        {documentTemplate.template_uri ? (
                                            <Button
                                                disableRipple
                                                size="small"
                                                startIcon={<Visibility fontSize="small" />}
                                                onClick={() =>
                                                    handleAction(
                                                        ACTIONS.PREVIEW_FILE,
                                                        documentTemplate
                                                    )
                                                }
                                            >
                                                {translate('preview')}
                                            </Button>
                                        ) : (
                                            '---'
                                        )}
                                    </TableCell>
                                    <TableCell sx={fitCellSx}>
                                        {documentTemplate.sample_uri ? (
                                            <Button
                                                disableRipple
                                                size="small"
                                                startIcon={<Visibility fontSize="small" />}
                                                onClick={() =>
                                                    handleAction(
                                                        ACTIONS.PREVIEW_SAMPLE,
                                                        documentTemplate
                                                    )
                                                }
                                            >
                                                {translate('preview')}
                                            </Button>
                                        ) : (
                                            '---'
                                        )}
                                    </TableCell>
                                    <TableCell align="right" sx={fitCellSx}>
                                        <Stack
                                            direction="row"
                                            spacing={1}
                                            sx={{ justifyContent: 'flex-end' }}
                                        >
                                            {!documentTemplate.has_been_requested && (
                                                <Button
                                                    size="small"
                                                    onClick={() =>
                                                        handleAction(
                                                            ACTIONS.SELECT,
                                                            documentTemplate
                                                        )
                                                    }
                                                >
                                                    {translate('Request Document')}
                                                </Button>
                                            )}

                                            <IconButton
                                                color="primary"
                                                size="small"
                                                onClick={() =>
                                                    handleAction(ACTIONS.EDIT, documentTemplate)
                                                }
                                            >
                                                <Mode fontSize="inherit" />
                                            </IconButton>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
            {descriptionModal}
        </>
    );
};

export default DocumentTemplateTable;
